import { useEffect, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  DatePickerProps,
  Empty,
  Menu,
  MenuProps,
  Modal,
  Segmented,
} from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { AllReviewsProps, menteeObjectiveProps } from '../../types';
import { Loading, NotificationAlert, Review } from '../../components';
import { useAtomValue } from 'jotai';
import { userAtom } from '../../store/atom';
import { getAllObjectives } from '../../services/objectiveServices';
import {
  quarterReviewStatuses,
  statusColors,
  Statuses,
  statusesLabel,
} from '../../helper';
import dayjs from 'dayjs';
import { useMsal } from '@azure/msal-react';
import {
  getAllQuarters,
  updateFeedbacks,
  getQuarterlyUserStatus,
} from '../../services/reviewsServices';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Paths } from '../../routes';
import { getQuarterTabLable } from '../../helper/functions';
import ViewUserTooltip from '../../components/view-user-tooltip';
import { getUserPhoto } from '../../helper/config';

interface quarterlyUserStatus {
  mentorStatus: string | undefined;
  menteeStatus: string | undefined;
  objectiveCount: number | undefined;
}

const QuarterlyReview = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { selectedMentee } = useAtomValue(userAtom);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [objectives, setObjectives] = useState<menteeObjectiveProps[]>([]);
  const [current, setCurrent] = useState<AllReviewsProps>();
  const { accounts } = useMsal();
  const from: 'hr' | 'mentor' | 'mentee' = state?.['from'];
  const ref = useRef<any>();
  const [allQuarters, setAllQuarters] = useState<AllReviewsProps[]>();
  const [filteredQuarters, setFilteredQuarters] = useState<AllReviewsProps[]>(
    []
  );
  const [canSubmit, setSubmit] = useState(false);
  const [canSave, setSave] = useState(false);
  const [menteeMentorStatus, setMenteeMentorStatus] = useState<
    quarterlyUserStatus | undefined | null
  >(null);
  const [feedbackValues, setFeedbackValues] = useState<{
    [key: string]: boolean;
  }>({});
  const [modal, contextHolder] = Modal.useModal();
  const [profile, setProfile] = useState<string | null>(null);
  const myRef = useRef<any>();
  const [selectedYear, setSelectedYear] = useState<string>(
    dayjs().format('YYYY')
  );

  // filter quarter by date and find active quarter
  const filterQuarter = (
    data: AllReviewsProps[] | undefined,
    date: string | number
  ) => {
    if (!data) return;
    setFilteredQuarters([]);

    const filter = data?.filter(
      (item) => item.year.toString() === date.toString() && item.status !== 0
    );

    if (filter.length === 0) {
      setFilteredQuarters(filter);
      return;
    }

    // Sort quarters by start date
    filter?.sort((a: AllReviewsProps, b: AllReviewsProps) =>
      dayjs(a.startDate).diff(dayjs(b.startDate))
    );

    setFilteredQuarters(filter);

    // Find active quarters (status === 1 means in progress)
    const activeQuarters = filter?.filter((item) => item.status === 1);

    if (activeQuarters && activeQuarters.length > 0) {
      // If multiple cycles are active, default to the most recent one
      activeQuarters.sort((a: any, b: any) =>
        dayjs(b.startDate).diff(dayjs(a.startDate))
      );
      setCurrent(activeQuarters[0]);
      const activeYear = dayjs(activeQuarters[0].startDate).format('YYYY');
      setSelectedYear(activeYear);
    } else {
      // If no active quarter, default to first quarter in filtered list
      setCurrent(filter[0]);
    }
  };

  // get all quarters
  const fetchAllQuarters = async () => {
    setLoading(true);
    const { data, error } = await getAllQuarters(
      from === 'mentee' ? undefined : from
    );
    setLoading(false);

    if (error) {
      myRef.current?.call('error', error?.message);
      return;
    }

    setAllQuarters(data);

    // Check for any active quarters across all years
    const allYearsQuarters = data || [];
    const activeQuarters = allYearsQuarters.filter(
      (quarter) => quarter.status === 1
    );

    if (activeQuarters.length > 0) {
      // Prefer the year of the most recently started active cycle
      activeQuarters.sort((a: any, b: any) =>
        dayjs(b.startDate).diff(dayjs(a.startDate))
      );
      const activeYear = dayjs(activeQuarters[0].startDate).format('YYYY');
      filterQuarter(data, activeYear);
    } else {
      // If no active quarter found, default to current year
      filterQuarter(data, dayjs().format('YYYY'));
    }
  };

  // fetch selected quarter objectives
  const fetchObjectives = async (id: string) => {
    setObjectives([]);
    setLoading(true);
    const { data, error } = await getAllObjectives(
      from !== 'mentee' ? selectedMentee?.id || '' : accounts[0].localAccountId,
      from === 'mentee' ? undefined : from,
      id
    );

    setLoading(false);
    if (error) return myRef.current?.call('error', error?.message);

    const response = await getQuarterlyUserStatus(
      from !== 'mentee' ? selectedMentee?.id || '' : accounts[0].localAccountId,
      id
    );

    if (response.error)
      return myRef.current?.call('error', response.error.message);

    setMenteeMentorStatus({
      menteeStatus: response.data?.menteeStatus,
      mentorStatus: response.data?.mentorStatus,
      objectiveCount: response.data?.user.objectiveCount,
    });

    setObjectives(data);
    computeSubmit(data, id);
  };

  // check feedback and enable submit button
  const computeSubmit = (data: menteeObjectiveProps[], id: string) => {
    setSubmit(false);
    let submit = true;
    data?.forEach((q) => {
      const findQuarterFeedback = q.objectiveComments?.findIndex(
        (c) => c.quarterId === id
      );
      if (findQuarterFeedback !== undefined && findQuarterFeedback !== -1) {
        const feedback =
          q.objectiveComments?.[findQuarterFeedback][
            from === 'mentee' ? 'menteeComment' : 'mentorComment'
          ];
        const status =
          q.objectiveComments?.[findQuarterFeedback][
            from === 'mentee' ? 'menteeCommentStatus' : 'mentorCommentStatus'
          ];
        if (feedback === null || feedback === '') submit = false;
        if (status === Statuses.Completed) submit = false;
      }
    });
    return setSubmit(submit);
  };

  const computeSave = (data: menteeObjectiveProps[], id: string) => {
    setSave(true);
    let save = true;
    const arr: any = [];
    data?.forEach((q) => {
      const findQuarterFeedback = q.objectiveComments?.findIndex(
        (c) => c.quarterId === id
      );
      if (findQuarterFeedback !== undefined && findQuarterFeedback !== -1) {
        if (
          q.objectiveComments?.[findQuarterFeedback]?.[
            from === 'mentee' ? 'menteeCommentStatus' : 'mentorCommentStatus'
          ] !== Statuses.Completed
        ) {
          const feedbackComment =
            q.objectiveComments?.[findQuarterFeedback][
              from === 'mentee' ? 'menteeComment' : 'mentorComment'
            ];
          arr.push(feedbackComment);
        }
      }
    });

    const isSave = arr.filter(
      (comment: any) => comment !== null && comment.trim() !== ''
    );
    save = isSave.length > 0;
    return setSave(save);
  };

  useEffect(() => {
    fetchAllQuarters();
  }, []);

  useEffect(() => {
    if (current?.id) {
      fetchObjectives(current?.id);
    }
  }, [current?.id]);

  useEffect(() => {
    if (!state) {
      navigate(`${Paths.MyProfile}`);
    }
  }, [state, navigate]);

  useEffect(() => {
    if (!selectedMentee || !selectedMentee?.id) return;
    getUserPhoto(selectedMentee?.id).then((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob);
        setProfile(imageUrl);
      }
    });
  }, [selectedMentee]);

  // set selected quarter
  const handleMenuClick = (selected: string) => {
    const find = filteredQuarters?.findIndex((item) => item.id === selected);
    if (find !== undefined && find !== -1) setCurrent(filteredQuarters?.[find]);
  };

  const onChange: DatePickerProps['onChange'] = (_date, dateString) => {
    if (typeof dateString === 'string') {
      setSelectedYear(dateString);
      filterQuarter(allQuarters, dateString);
    }
  };

  const handleTabNav = (val: string) => {
    if (val === 'Objectives Dashboard') {
      return navigate(-1);
    }
  };

  const handleReviewChange = (data: menteeObjectiveProps) => {
    // check if updated objected is already exist in array
    //setSave(true);
    const findObjective = objectives.findIndex((i) => i.id === data.id);
    const temp = [...objectives];
    temp[findObjective] = { ...data };
    setObjectives(temp);
    computeSave(temp, current?.id || '');
    computeSubmit(temp, current?.id || '');
  };

  // to disable save button if no changes
  const handleFeedbackValueChange = (
    id: string | undefined,
    hasValue: boolean
  ) => {
    if (id) setFeedbackValues((prev) => ({ ...prev, [id]: hasValue }));
  };

  // submit feedbacks
  const handleSubmit = async (status: string) => {
    setUpdateLoading(true);
    const { error } = await updateFeedbacks({
      quarterlyObjectivesList: objectives,
      commentStatus: status, //Statuses.SubmittedToHR,
      quarterId: current?.id || '',
    });
    setUpdateLoading(false);
    if (error) return myRef.current?.call('error', error?.message);
    ref.current?.call('success');
    setSave(false);
    fetchObjectives(current?.id || '');
  };

  const toSentenceCase = (str: string | null | undefined): string => {
    // Replace underscores with spaces

    if (!str) return '';
    let result = str.replace(/_/g, ' ');

    // Convert to sentence case (each word's first letter uppercase)
    result = result.toLowerCase().replace(/\b\w/g, function (match) {
      return match.toUpperCase();
    });

    return result;
  };

  const getQuarterTabTopic = (
    startDate: string | undefined,
    endDate: string | undefined
  ): string => {
    if (!startDate || !endDate) return 'Quarter';

    const startMonth = new Date(startDate).getMonth() + 1; // getMonth() returns 0-11
    const endMonth = new Date(endDate).getMonth() + 1; // getMonth() returns 0-11

    const startYear = new Date(startDate).getFullYear();

    if (startYear <= 2024) {
      if (startMonth === 1 && endMonth === 3)
        return `${startYear - 1} Quarter 4 Feedback`;
      if (startMonth === 4 && endMonth === 6)
        return `${startYear} Quarter 1 Feedback`;
      if (startMonth === 7 && endMonth === 9)
        return `${startYear} Quarter 2 Feedback`;
      if (startMonth === 10 && endMonth === 12)
        return `${startYear} Quarter 3 Feedback`;
    } else {
      if (startMonth === 1 && endMonth === 3)
        return `${startYear} Quarter 1 Feedback`;
      if (startMonth === 4 && endMonth === 6)
        return `${startYear} Quarter 2 Feedback`;
      if (startMonth === 7 && endMonth === 9)
        return `${startYear} Quarter 3 Feedback`;
      if (startMonth === 10 && endMonth === 12)
        return `${startYear} Quarter 4 Feedback`;
    }

    return 'Quarter';
  };

  const handleConfirmSubmit = (status: string) => {
    const deadline =
      from === 'mentee'
        ? current?.menteeDeadline
        : from === 'mentor'
        ? current?.mentorDeadline
        : null;

    const isEarly = deadline ? dayjs().isBefore(dayjs(deadline), 'day') : false;

    const confirmationTitle = isEarly
      ? 'Are you sure you want to submit your feedback, as the quarterly feedback cycle is still in progress?'
      : 'Are you sure you want to submit your feedback?';

    modal.confirm({
      title: confirmationTitle,
      icon: <ExclamationCircleFilled />,
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk() {
        return handleSubmit(status);
      },
    });
  };

  return (
    <div className="flex flex-col gap-y-[20px]">
      <div className="flex flex-col">
        <div className="flex">
          <div className="text-[20px] font-[500] flex gap-x-2 items-center">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />{' '}
            <ViewUserTooltip
              user={{
                name: selectedMentee?.name,
                mentor: selectedMentee?.mentor,
                designation: selectedMentee?.designation,
                tier: selectedMentee?.tier,
                profilePic: profile,
              }}
            />
          </div>
          <div className="ml-auto">
            <Segmented
              options={['Objectives Dashboard', 'Quarterly Feedback']}
              defaultValue="Quarterly Feedback"
              onChange={handleTabNav}
            />
            <DatePicker
              onChange={onChange}
              picker="year"
              className="ml-3 w-fit"
              placeholder="Year"
              value={selectedYear ? dayjs(selectedYear) : undefined}
              disabled={loading}
            />
          </div>
        </div>
      </div>
      {loading && !allQuarters ? (
        <Loading />
      ) : filteredQuarters.length === 0 ? (
        <Empty />
      ) : (
        <div>
          <Quarters
            onClick={handleMenuClick}
            items={filteredQuarters as AllReviewsProps[]}
            current={current as AllReviewsProps}
          />
          <div className="flex">
            <div className="text-[20px] font-[500] p-[10px]">
              {getQuarterTabTopic(current?.startDate, current?.endDate)}
            </div>
            <div className="flex ml-auto gap-x-2 ">
              {current ? (
                <>
                  <div className="flex flex-col p-[5px]">
                    <div className="opacity-50">Mentee Deadline</div>
                    <div className="opacity-85">
                      {dayjs(current.menteeDeadline).format('MMM DD YYYY')}
                    </div>
                  </div>
                  <div className="flex flex-col p-[5px]">
                    <div className="opacity-50">Mentor Deadline</div>
                    <div className="opacity-85">
                      {dayjs(current.mentorDeadline).format('MMM DD YYYY')}
                    </div>
                  </div>

                  {menteeMentorStatus?.menteeStatus && (
                    <div
                      style={{
                        background: `${statusColors[menteeMentorStatus?.menteeStatus] || ''}`,
                      }}
                      className="flex flex-col rounded-[5px] text-white p-[5px]"
                    >
                      <div>Mentee Status</div>
                      <div className="text-base font-bold">
                        {toSentenceCase(menteeMentorStatus?.menteeStatus)}
                      </div>
                    </div>
                  )}

                  {menteeMentorStatus?.mentorStatus && (
                    <div
                      style={{
                        background: `${statusColors[menteeMentorStatus?.mentorStatus] || ''}`,
                      }}
                      className="flex flex-col rounded-[5px] text-white p-[5px]"
                    >
                      <div>Mentor Status</div>
                      <div className="text-base font-bold">
                        {toSentenceCase(menteeMentorStatus?.mentorStatus)}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      background: `${statusColors[quarterReviewStatuses?.[current.status]] || ''}`,
                    }}
                    className="flex flex-col rounded-[5px] text-white p-[5px]"
                  >
                    <div>Quarterly Feedback Status</div>
                    <div className="text-base font-bold">
                      {statusesLabel[quarterReviewStatuses?.[current.status]]}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {objectives.length > 0 && filteredQuarters.length !== 0 ? (
        <div>
          <div className="flex flex-col gap-y-[16px]">
            {objectives?.map((obj, idx: number) => (
              <Review
                data={obj as menteeObjectiveProps}
                key={idx}
                from={from}
                quarter={current as AllReviewsProps}
                onChange={handleReviewChange}
                onFeedbackChange={(id, hasValue) =>
                  handleFeedbackValueChange(id, hasValue)
                }
              />
            ))}
          </div>
          {from === 'hr' ? null : (
            <div className="flex justify-end mt-4">
              <div className="flex gap-x-[10px]">
                <Button
                  disabled={!canSave || current?.status === 3}
                  onClick={() => handleSubmit(Statuses.InProgress)}
                  loading={updateLoading}
                >
                  Save
                </Button>
                <Button
                  type="primary"
                  disabled={
                    !canSubmit ||
                    updateLoading ||
                    Object.values(feedbackValues).some(
                      (hasValue) => !hasValue
                    ) ||
                    current?.status === 3
                  }
                  onClick={() => handleConfirmSubmit(Statuses.Completed)}
                >
                  Submit Feedback
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
      {contextHolder}
      <NotificationAlert ref={ref} />
    </div>
  );
};

export default QuarterlyReview;

const Quarters = ({
  onClick,
  items,
  current,
}: {
  onClick(val: string): void;
  items: AllReviewsProps[];
  current: AllReviewsProps;
}) => {
  const handleClick: MenuProps['onClick'] = (e) => {
    onClick(e.key);
  };

  return (
    <Menu
      onClick={handleClick}
      defaultOpenKeys={[current?.id]}
      selectedKeys={[current?.id]}
      mode="horizontal"
      items={items?.map((item) => ({
        key: item.id,
        label: getQuarterTabLable(item.startDate, item.endDate),
      }))}
      className="border-0"
      disabledOverflow
    />
  );
};
